import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarDays, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const ALL = "__ALL__";

function FSelect({ testid, placeholder, value, options, onChange }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-9 rounded-sm border-slate-300 bg-white text-sm !w-auto min-w-[130px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-sm">
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DateField({ testid, label, value, onChange }) {
  const selected = value ? parseISO(value) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testid}
          className="h-9 rounded-sm border-slate-300 bg-white text-sm font-normal gap-2 justify-start min-w-[120px]">
          <CalendarDays size={14} className="text-slate-400" />
          {value ? <span className="font-mono">{value}</span> : <span className="text-slate-400">{label}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-sm" align="start">
        <Calendar mode="single" locale={es} selected={selected}
          onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} />
      </PopoverContent>
    </Popover>
  );
}

export function FiltersBar({ options, filters, setFilters, showStatus = true, slaDays, setSlaDays }) {
  const opt = options || {};
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div className="bg-[#F8F9FA] border-b border-[#E2E8F0] px-4 sm:px-6 py-3" data-testid="filters-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="filter-search" value={filters.search || ""}
            onChange={(e) => set({ search: e.target.value })}
            placeholder="Order #, cliente, modelo…"
            className="h-9 rounded-sm border-slate-300 bg-white text-sm pl-8 w-[210px]" />
        </div>

        {showStatus && (
          <FSelect testid="filter-status" placeholder="Estatus"
            value={filters.status} options={opt.statuses || []}
            onChange={(v) => set({ status: v })} />
        )}
        <FSelect testid="filter-route" placeholder="Ruta" value={filters.route}
          options={opt.routes || []} onChange={(v) => set({ route: v })} />
        <FSelect testid="filter-order-type" placeholder="Tipo Orden" value={filters.order_type}
          options={opt.order_types || []} onChange={(v) => set({ order_type: v })} />
        <FSelect testid="filter-origin" placeholder="Origen" value={filters.origin}
          options={opt.origins || []} onChange={(v) => set({ origin: v })} />
        <FSelect testid="filter-delivery-type" placeholder="Entrega" value={filters.delivery_type}
          options={opt.delivery_types || []} onChange={(v) => set({ delivery_type: v })} />

        <Select value={filters.sla || ALL} onValueChange={(v) => set({ sla: v === ALL ? "" : v })}>
          <SelectTrigger data-testid="filter-sla" className="h-9 rounded-sm border-slate-300 bg-white text-sm !w-auto min-w-[130px]">
            <SelectValue placeholder="SLA" />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value={ALL}>SLA: Todos</SelectItem>
            <SelectItem value="FUERA_SLA">Fuera de SLA</SelectItem>
            <SelectItem value="EN_RIESGO">En riesgo</SelectItem>
            <SelectItem value="EN_SLA">En SLA</SelectItem>
          </SelectContent>
        </Select>

        {setSlaDays && (
          <Select value={String(slaDays)} onValueChange={(v) => setSlaDays(Number(v))}>
            <SelectTrigger data-testid="filter-sla-days" className="h-9 rounded-sm border-slate-300 bg-white text-sm !w-auto min-w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm">
              {[3, 7, 14, 30, 60, 90, 120].map((d) => <SelectItem key={d} value={String(d)}>SLA {d} días</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <DateField testid="filter-date-from" label="Desde" value={filters.date_from}
          onChange={(v) => set({ date_from: v })} />
        <DateField testid="filter-date-to" label="Hasta" value={filters.date_to}
          onChange={(v) => set({ date_to: v })} />
      </div>
    </div>
  );
}
